/**
 * The verb menu — what you may do to the body at your feet (M2-07).
 *
 * Built to `AimMenu`'s pattern deliberately, because the pattern is right and a
 * second one would be a second thing to learn: **one key to open, one key to
 * choose, and it is done**. The menu never stays open waiting to be escaped
 * from, and it never asks a question it could answer itself.
 *
 * The one departure is `execute`, which asks twice. That is not inconsistency —
 * it is the only verb here that cannot be undone, and a killing blow you can
 * arrive at with a single mistyped keystroke is a killing blow that will happen
 * by accident on the first evening of the playtest. The confirmation is also
 * checked by the *server* (`parseExecute` demands `confirm: true`), because a
 * rule only the client enforces lasts until somebody writes their own client;
 * this is the half that makes it feel deliberate rather than the half that
 * makes it safe.
 *
 * Pure state, like the aim menu: it knows nothing about the DOM and nothing
 * about the socket. `game.ts` opens it, feeds it keys, and sends what it
 * returns.
 */

import type { EntitySnapshot } from '@annwn/protocol';

/**
 * The verbs, in the order the prompt lists them.
 *
 * Rescue first, robbery second, killing last. That ordering is not decoration:
 * it is the order a decent person considers them in, and the destructive one
 * should never be the one nearest the finger that just opened the menu.
 */
export const INTERACT_KEYS: readonly { readonly key: string; readonly verb: InteractVerb }[] = [
  { key: 's', verb: 'staunch' },
  { key: 'b', verb: 'bandage' },
  // M4-05. The surgery reaches a fallen body through the same door as the rest
  // of the ladder. `amputate` sits beside `execute` at the end and asks twice
  // for the same reason: it cannot be undone, and on a body that cannot answer
  // there is nothing else standing between a keystroke and somebody's leg.
  { key: 'i', verb: 'extract' },
  { key: 'l', verb: 'loot' },
  { key: 'd', verb: 'drag' },
  // M4-06. Carrying is the better half of the rescue and costs you your hands;
  // it sits beside dragging because that is the choice being made.
  { key: 'c', verb: 'carry' },
  { key: 'v', verb: 'amputate' },
  { key: 'x', verb: 'execute' },
];

export type InteractVerb =
  'staunch' | 'bandage' | 'extract' | 'loot' | 'drag' | 'carry' | 'amputate' | 'execute';

const VERB_OF_KEY = new Map(INTERACT_KEYS.map((entry) => [entry.key, entry.verb]));

/** The word the prompt shows. Plain verbs — no euphemism for the last one. */
const LABEL: { readonly [V in InteractVerb]: string } = {
  staunch: 'staunch',
  bandage: 'bind',
  extract: 'dig out',
  loot: 'rob',
  drag: 'drag',
  carry: 'carry',
  amputate: 'take it off',
  execute: 'finish',
};

/** The verbs that ask twice. Both are irreversible; neither is a mistake worth making. */
const CONFIRMS: ReadonlySet<InteractVerb> = new Set<InteractVerb>(['amputate', 'execute']);

/** What the menu resolves to: a verb against a body, once confirmed. */
export interface InteractChoice {
  readonly verb: InteractVerb;
  readonly targetId: string;
}

/** A one-line prompt: `staunch (s) · rob (l) · drag (d) · finish (x)`. */
export function interactPrompt(target: EntitySnapshot): string {
  const offered = INTERACT_KEYS.map(({ key, verb }) => `${LABEL[verb]} (${key})`).join(' · ');
  return `${target.name}: ${offered}`;
}

/** The confirmation line. Names them, because that is what makes it land. */
export function confirmPrompt(target: EntitySnapshot, verb: InteractVerb = 'execute'): string {
  return verb === 'amputate'
    ? `Take ${target.name}'s limb? They cannot agree to this. (v again to confirm)`
    : `Finish ${target.name}? This kills them. (x again to confirm)`;
}

/**
 * Is this body one the verbs apply to?
 *
 * Read off the snapshot's `down` flag rather than guessed from `prone`: the two
 * became different things in M2-07, and a menu that opened on everyone who had
 * been knocked over would offer to rob people who are about to stand up.
 */
export function isHelplessTarget(entity: EntitySnapshot): boolean {
  return entity.combat?.down ?? false;
}

/**
 * The menu, as a small state machine.
 *
 * Two states rather than the aim menu's one, and only because of `execute`:
 * `open` is the verb list, `confirming` is the second press. Anything
 * longer-lived would fight the chat line and the look cursor for the keyboard,
 * which is the bug `setChatOpen` already exists to avoid.
 */
export class InteractMenu {
  private target: EntitySnapshot | null = null;
  private confirming: InteractVerb | null = null;

  get active(): boolean {
    return this.target !== null;
  }

  /** Open against a body. Refused when there is nothing to work on. */
  show(target: EntitySnapshot): string | null {
    if (!isHelplessTarget(target)) return null;
    this.target = target;
    this.confirming = null;
    return interactPrompt(target);
  }

  close(): void {
    this.target = null;
    this.confirming = null;
  }

  /**
   * Feed it a key.
   *
   * Returns the choice to send, or `null`. Closes on anything that is not the
   * `execute` key opening a confirmation — so, exactly like the aim menu, a
   * mistyped key is one press and not two. `prompt` is the line to show when
   * the menu is staying open to ask again.
   */
  choose(key: string): { choice: InteractChoice | null; prompt: string | null } {
    const target = this.target;
    if (!target) return { choice: null, prompt: null };

    const verb = VERB_OF_KEY.get(key.toLowerCase()) ?? null;

    // The second press. Only reachable from the confirming state, so a single
    // keystroke can never kill anybody — or take their leg.
    const awaiting = this.confirming;
    if (awaiting) {
      this.close();
      return verb === awaiting
        ? { choice: { verb: awaiting, targetId: target.id }, prompt: null }
        : { choice: null, prompt: null };
    }

    if (verb !== null && CONFIRMS.has(verb)) {
      this.confirming = verb;
      return { choice: null, prompt: confirmPrompt(target, verb) };
    }

    this.close();
    return verb === null
      ? { choice: null, prompt: null }
      : { choice: { verb, targetId: target.id }, prompt: null };
  }
}
